import { Link } from 'react-router';
import { screeningLinkMap } from '@/types/dashboard';
import type { TLocalize } from '@hooks/use-localize';
import type { INkrCenterOption } from '@/types/history/nkr-centers';
import Popover from '@components/popover';

interface IProps {
    center: INkrCenterOption;
    t: TLocalize;
    registrationId: number;
    editKey: string;
    isFemale: boolean;
    isLiver: boolean;
    isSubmitting: boolean;
    onSelect: (center: INkrCenterOption) => void;
}

const CenterRow = ({ center, t, registrationId, editKey, isFemale, isLiver, isSubmitting, onSelect }: IProps) => {
    const rowClass = 'bs';

    const buttonClass = center.hasHardErrors ?
        'btn-link text-ineligible'
        :
        center.hasSoftErrors ? 'btn-warning' : 'btn-success';

    const dotColor = center.hasHardErrors ? null : center.hasSoftErrors ? '#f4a300' : '#0a9c20';

    const selectButton = (
        <button
            type="button"
            className={'btn ' + buttonClass + ' center-selection-btn col-xs-12'}
            disabled={isSubmitting}
            onClick={() => onSelect(center)}
        >
            {t('DashLabel_Select', 'Select')}
        </button>
    );

    const ineligibleButton = (
        <Popover
            trigger="click"
            block={true}
            content={
                <RuleOutContent
                    center={center}
                    registrationId={registrationId}
                    editKey={editKey}
                    isFemale={isFemale}
                    isLiver={isLiver}
                />
            }
        >
            <button
                type="button"
                className={'btn ' + buttonClass + ' center-selection-btn col-xs-12'}
                style={{ textDecoration: 'underline' }}
            >
                <span className="fas fa-info-circle icon-nkr-red"></span>{' '}
                {t('DashLabel_Ineligible', 'Ineligible')}
            </button>
        </Popover>
    );

    return (
        <tr className={rowClass}>
            <td className='center-name'>
                {dotColor ?
                    <span className='center-dot' style={{ backgroundColor: dotColor }}></span>
                    :
                    <span className='center-dot empty-center-dot'></span>
                }

                <span style={{ color: dotColor ? dotColor : '', fontWeight: dotColor ? 'bold' : 'normal' }}>{center.name}</span>
            </td>
            <td className="hidden-xs">
                {center.distanceFromCenter != null ? center.distanceFromCenter : '--'}
            </td>
            <td className="hidden-xs">{center.state}</td>
            <td className="hidden-xs">{center.industryLdTransplantsViaNkr || '--'}</td>
            <td className="hidden-xs">
                {center.isAdvanceReimbursementEligible && (
                    <span
                        className="fas fa-check"
                        title={t('Word_Yes', 'Yes')}
                        aria-label={t('Word_Yes', 'Yes')}
                        role="img"
                    ></span>
                )}
            </td>
            <td>{center.hasHardErrors ? ineligibleButton : selectButton}</td>
        </tr>
    );
};

interface IRuleOutContentProps {
    center: INkrCenterOption;
    registrationId: number;
    editKey: string;
    isFemale: boolean;
    isLiver: boolean;
}

const RuleOutContent = ({ center, registrationId, editKey, isFemale, isLiver }: IRuleOutContentProps) => {
    const step = screeningLinkMap(isFemale, isLiver).find((x) => x.step === center.hardRuleOutStep);
    const updateLink = step
        ? step.link + '/' + registrationId + '/' + editKey
        : '/register/donate/' + registrationId + '/' + editKey;

    return (
        <>
            <p style={{ marginBottom: 5 }}>
                Based on the details you&apos;ve provided, you are not eligible to apply at this
                center because:
            </p>
            <ul style={{ paddingLeft: 20, marginBottom: 10 }}>
                {center.hardRuleOuts.map((reason, index) => (
                    <li key={index}>{reason}</li>
                ))}
            </ul>
            <Link to={updateLink}>Update your screening answers</Link>
        </>
    );
};

export default CenterRow;
